import { Container, Graphics, Text } from "pixi.js";
import { z } from "zod";
import { FONT_OPTIONS } from "../src/fonts/font-loader";
import { createDomOverlay, type DomOverlayHandle } from "../src/widgets/dom-overlay";
import { colorToCss } from "../src/widgets/format";
import { createScrollableContent } from "../src/widgets/scrollable-content";
import {
  isTransparent,
  safeColor,
  type CompactInfo,
  type WidgetController,
  type WidgetFactory,
  type WidgetMountContext,
} from "../src/widgets/widget-types";

const PADDING = 10;
const BORDER_EDITING_COLOR = 0xd946ef;
const PLACEHOLDER_COLOR = 0x94a3b8;

/** border colors that complement the dark notepad background */
const NOTEPAD_BORDER_COLORS = [
  0xcbd5e1, 0xa5b4fc, 0xc084fc, 0xf472b6, 0xfb923c, 0xfbbf24, 0x4ade80, 0x2dd4bf, 0x38bdf8,
  0x818cf8, 0x94a3b8, 0x6366f1, 0x8b5cf6, 0xec4899, 0xf97316, 0x22c55e, 0x14b8a6, 0x3b82f6,
  0xef4444, 0xeab308,
];

function randomBorderColor(): number {
  return NOTEPAD_BORDER_COLORS[Math.floor(Math.random() * NOTEPAD_BORDER_COLORS.length)];
}

export const notepadSchema = z.object({
  text: z.string().default(""),
  bgColor: z.number().default(0x1e1e2e),
  textColor: z.number().default(0xf8fafc),
  borderColor: z.number().default(() => randomBorderColor()),
  fontSize: z.number().default(13),
  fontFamily: z.string().default("system-ui, sans-serif"),
});

export type NotepadState = z.infer<typeof notepadSchema>;

export const notepadWidget: WidgetFactory<typeof notepadSchema> = {
  type: "notepad",
  metadata: {
    name: "notepad",
    description: "a multi-line text notepad with word wrap",
    version: "0.1.0",
    category: "text",
  },
  schema: notepadSchema,
  editableProps: [
    { key: "bgColor", label: "background", type: "color" as const, default: 0x1e1e2e },
    { key: "textColor", label: "text color", type: "color" as const, default: 0xf8fafc },
    { key: "borderColor", label: "border", type: "color" as const, default: 0xcbd5e1 },
    { key: "fontSize", label: "font size", type: "number" as const, default: 13 },
    {
      key: "fontFamily",
      label: "font",
      type: "select" as const,
      options: FONT_OPTIONS,
      default: "system-ui, sans-serif",
    },
  ],

  getCompactInfo: (state: NotepadState): CompactInfo => ({
    label: state.text ? state.text.split("\n")[0] : "empty note",
  }),

  create(ctx: WidgetMountContext<typeof notepadSchema>): WidgetController {
    const container = new Container();
    let editing = false;
    let currentWidth = ctx.width;
    let currentHeight = ctx.height;

    // background
    const bg = new Graphics();
    const drawBg = (w: number, h: number, isEditing: boolean) => {
      const state = ctx.doc.current;
      bg.clear();
      bg.roundRect(0, 0, w, h, 6);
      bg.fill(state.bgColor === -1 ? { color: 0, alpha: 0 } : { color: state.bgColor });
      const strokeColor = isEditing ? BORDER_EDITING_COLOR : state.borderColor;
      const strokeWidth = isEditing ? 3 : 1;
      bg.stroke(
        strokeColor === -1
          ? { color: 0, alpha: 0, width: strokeWidth }
          : { color: strokeColor, width: strokeWidth }
      );
    };
    drawBg(currentWidth, currentHeight, false);
    container.addChild(bg);

    // scrollable content area — holds text; overflow scrolls instead of
    // being silently clipped (see scrollable-content.ts for the wheel/pan
    // handling this needs to coexist with the canvas viewport's own pan).
    const contentWidth = () => Math.max(currentWidth - PADDING * 2, 1);
    const contentHeight = () => Math.max(currentHeight - PADDING * 2, 1);
    const scrollable = createScrollableContent(
      container,
      ctx.canvasElement,
      PADDING,
      PADDING,
      contentWidth(),
      contentHeight()
    );
    const content = scrollable.content;
    content.cursor = "text";

    const reflow = () => {
      const h = Math.max(textDisplay.height, placeholder.height);
      scrollable.reflow(contentWidth(), h);
    };

    const textDisplay = new Text({
      text: ctx.doc.current.text,
      style: {
        fontFamily: ctx.doc.current.fontFamily,
        fontSize: ctx.doc.current.fontSize,
        fill: safeColor(ctx.doc.current.textColor),
        wordWrap: true,
        wordWrapWidth: contentWidth(),
      },
    });
    content.addChild(textDisplay);
    textDisplay.alpha = isTransparent(ctx.doc.current.textColor) ? 0 : 1;

    // placeholder text
    const placeholder = new Text({
      text: "double-click to type...",
      style: {
        fontFamily: ctx.doc.current.fontFamily,
        fontSize: ctx.doc.current.fontSize,
        fontStyle: "italic",
        fill: PLACEHOLDER_COLOR,
      },
    });
    content.addChild(placeholder);

    const updatePlaceholderVisibility = () => {
      const hasText = textDisplay.text.length > 0;
      placeholder.visible = !hasText && !editing;
    };

    const updateWordWrap = () => {
      textDisplay.style.wordWrapWidth = contentWidth();
    };

    updatePlaceholderVisibility();
    reflow();

    // DOM overlay for inline editing
    let activeOverlay: DomOverlayHandle | null = null;

    const startEditing = () => {
      if (editing) return;
      if (ctx.canvasStore?.isLocalViewer()) return;
      editing = true;
      drawBg(currentWidth, currentHeight, true);
      textDisplay.visible = false;
      placeholder.visible = false;

      const state = ctx.doc.current;

      activeOverlay = createDomOverlay({
        container,
        canvasElement: ctx.canvasElement,
        width: currentWidth,
        height: currentHeight,
        multiline: true,
        value: state.text,
        enterCommits: false, // Enter inserts newlines in notepad
        onCommit: (value: string) => {
          editing = false;
          activeOverlay = null;
          if (value !== ctx.doc.current.text) {
            ctx.doc.change((draft) => {
              draft.text = value;
            });
          }
          textDisplay.text = ctx.doc.current.text;
          textDisplay.visible = true;
          drawBg(currentWidth, currentHeight, false);
          updatePlaceholderVisibility();
          reflow();
        },
        onRevert: () => {
          editing = false;
          activeOverlay = null;
          textDisplay.text = ctx.doc.current.text;
          textDisplay.visible = true;
          drawBg(currentWidth, currentHeight, false);
          updatePlaceholderVisibility();
          reflow();
        },
        css: {
          fontFamily: state.fontFamily,
          fontSize: `${state.fontSize}px`,
          color: colorToCss(state.textColor),
          padding: `${PADDING}px`,
          overflow: "auto",
          lineHeight: "1.4",
          whiteSpace: "pre-wrap",
          wordWrap: "break-word",
        },
      });
    };

    // double-click to enter edit mode. registered on both `bg` (the border
    // area outside the scrollable content) and `content` (the scrollable
    // area itself, which now sits on top of `bg` and would otherwise
    // swallow clicks that land on the text) — a click on either fires the
    // same handler.
    let lastTapTime = 0;
    const handleDoubleTap = () => {
      if (editing) return;
      const now = Date.now();
      if (now - lastTapTime < 400) {
        startEditing();
        lastTapTime = 0;
      } else {
        lastTapTime = now;
      }
    };
    bg.eventMode = "static";
    bg.cursor = "text";
    bg.on("pointertap", handleDoubleTap);
    content.on("pointertap", handleDoubleTap);

    // subscribe to remote doc changes
    const unsub = ctx.doc.on("change", (state) => {
      if (!editing) {
        textDisplay.text = state.text;
      }
      // apply style changes
      textDisplay.style.fill = safeColor(state.textColor);
      textDisplay.alpha = isTransparent(state.textColor) ? 0 : 1;
      textDisplay.style.fontFamily = state.fontFamily;
      textDisplay.style.fontSize = state.fontSize;
      placeholder.style.fontFamily = state.fontFamily;
      placeholder.style.fontSize = state.fontSize;
      drawBg(currentWidth, currentHeight, editing);
      updatePlaceholderVisibility();
      reflow();
    });

    return {
      container,

      onReposition() {
        activeOverlay?.reposition();
      },

      destroy() {
        if (activeOverlay) {
          activeOverlay.remove();
          activeOverlay = null;
        }
        unsub();
        scrollable.destroy();
        container.destroy({ children: true });
      },

      resize(width: number, height: number) {
        if (editing && activeOverlay) {
          activeOverlay.element.blur();
        }
        currentWidth = width;
        currentHeight = height;
        drawBg(width, height, false);
        scrollable.resize(contentWidth(), contentHeight());
        updateWordWrap();
        reflow();
      },
    };
  },
};
